import axios from "axios";

/**
 * API_BASE selection:
 *  - En preview/local: usa REACT_APP_BACKEND_URL (apunta al backend del preview)
 *  - En producción (dominio custom como dispatch.findmed.com.mx): usa "/api" relativo,
 *    así Emergent proxea internamente al backend en el mismo dominio y las cookies
 *    httpOnly funcionan sin problemas de cross-origin/SameSite.
 *
 * Detección: si el hostname actual no coincide con el del REACT_APP_BACKEND_URL del build,
 * asumimos producción con custom domain y usamos same-origin.
 */
function resolveApiBase() {
    const envUrl = process.env.REACT_APP_BACKEND_URL || "";
    if (typeof window === "undefined") return `${envUrl}/api`;
    try {
        const envHost = envUrl ? new URL(envUrl).hostname : "";
        const currentHost = window.location.hostname;
        // Misma host (preview o local) -> usa env URL
        if (envHost && envHost === currentHost) return `${envUrl}/api`;
        // Diferente host -> production con custom domain -> same-origin
        return "/api";
    } catch {
        return envUrl ? `${envUrl}/api` : "/api";
    }
}

export const API_BASE = resolveApiBase();

export const api = axios.create({
    baseURL: API_BASE,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

let refreshPromise = null;

api.interceptors.response.use(
    (r) => r,
    async (error) => {
        const original = error.config || {};
        const status = error?.response?.status;
        const url = original.url || "";

        // /auth/me 401 = simply not logged in. Return null silently so AuthContext can set user=false.
        if (status === 401 && url.includes("/auth/me")) {
            return { data: null };
        }

        // For other authenticated endpoints: try refresh once, else surface error.
        if (status === 401 && !original._retry && !url.includes("/auth/")) {
            original._retry = true;
            try {
                if (!refreshPromise) {
                    refreshPromise = axios.post(`${API_BASE}/auth/refresh`, {}, { withCredentials: true });
                }
                await refreshPromise;
                refreshPromise = null;
                return api(original);
            } catch (_e) {
                refreshPromise = null;
                // Sólo redirige si no estamos ya en /login para evitar loops.
                // Y sólo si NO estamos justo después de un login (Auth en context).
                if (typeof window !== "undefined" && !window.location.pathname.includes("/login")) {
                    // Damos 500ms para que cualquier setState/navigate en curso termine.
                    setTimeout(() => {
                        if (!window.location.pathname.includes("/login")) {
                            window.location.replace("/login?reason=session_expired");
                        }
                    }, 500);
                }
                return Promise.reject(error);
            }
        }

        return Promise.reject(error);
    }
);

// Silence dev-overlay for known/expected rejections
if (typeof window !== "undefined") {
    window.addEventListener("unhandledrejection", (event) => {
        const reason = event.reason;
        if (reason?.isAxiosError || reason?.response?.status === 401) {
            event.preventDefault();
        }
    });
}

export function formatApiError(detail) {
    if (detail == null) return "Algo salió mal. Intenta de nuevo.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export const money = (n) =>
    new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 }).format(n || 0);

export const numFmt = (n) => new Intl.NumberFormat("es-MX").format(n || 0);

export const dateFmt = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" });
    } catch {
        return iso;
    }
};

export const dateOnly = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("es-MX");
    } catch {
        return iso;
    }
};

// Auto-added default export for backward compatibility
export default api;
